import { useQueryClient } from '@tanstack/react-query';
import { Modal } from 'components/Modal';
import { usePrincipalById } from 'hooks/queries/usePrincipalById';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { updatePrincipalInformation } from 'utils/principal-service/principal-endpoints';
import {
  PrincipalInformationAndFeatures,
  PrincipalModalTypes,
} from 'utils/types';
import { Tab, Tabs } from '../Tabs';
import { EditDataAccess } from './EditDataAccess';
import { EditPrincipalInformation } from './EditPrincipalInformation';

enum principalInformationTabConfigs {
  dataAccess = 'data-access',
  principalInformation = 'principal-information',
}

export interface EditPrincipalModalProps {
  principalId: string;
  onClose: () => void;
}

export interface EditPrincipalAccessProps {
  principalInformation: PrincipalInformationAndFeatures | null;
  principalModalType?: PrincipalModalTypes;
  onChange: (principalInformation: PrincipalInformationAndFeatures) => void;
}

export const EditPrincipalModal: React.FC<EditPrincipalModalProps> = ({
  principalId,
  onClose,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data: initialPrincipalInformation } = usePrincipalById(principalId);
  const [principalInformation, setPrincipalInformation] =
    useState<PrincipalInformationAndFeatures | null>(
      initialPrincipalInformation || null,
    );

  useEffect(() => {
    if (initialPrincipalInformation && !principalInformation) {
      setPrincipalInformation(initialPrincipalInformation);
    }
  }, [initialPrincipalInformation, principalInformation]);

  const [selectedTab, setSelectedTab] =
    useState<principalInformationTabConfigs>(
      principalInformationTabConfigs.principalInformation,
    );

  const handleConfirm = async () => {
    if (!principalInformation) {
      return;
    }

    try {
      await updatePrincipalInformation({
        ...principalInformation,
        id: principalId,
      });
      queryClient.invalidateQueries({
        queryKey: ['principals'],
      });
      queryClient.invalidateQueries({
        queryKey: ['principalById', principalId],
      });
      queryClient.invalidateQueries({
        queryKey: ['principalInformation', principalId],
      });
      queryClient.invalidateQueries({
        queryKey: ['principalFeatureAccessRights'],
      });
      setPrincipalInformation(null);
      onClose();
    } catch (error) {
      console.error(error);
    }
  };

  const handleCancel = () => {
    setPrincipalInformation(null);
    onClose();
  };

  return (
    <Modal
      isBig
      isCancelButtonHidden={false}
      headingAlign="left"
      heading={t(
        'features:admin-center:principalAdministration:editPrincipalModal:title',
        {
          principalName: principalInformation?.name,
        },
      )}
      confirmText={t(
        'features:admin-center:principalAdministration:editPrincipalModal:save',
      )}
      onConfirm={handleConfirm}
      onCancel={handleCancel}
    >
      <div className="w-full">
        <div className="max-w-xl-content mx-auto">
          <Tabs
            activeTab={selectedTab}
            onTabChange={(tab) =>
              setSelectedTab(tab as principalInformationTabConfigs)
            }
          >
            <Tab
              key={principalInformationTabConfigs.principalInformation}
              tabKey={principalInformationTabConfigs.principalInformation}
              title={t(
                `features:admin-center:principalAdministration:editPrincipalModal:${principalInformationTabConfigs.principalInformation}:title`,
              )}
            >
              <EditPrincipalInformation
                principalModalType={PrincipalModalTypes.EDIT}
                principalInformation={principalInformation}
                onChange={setPrincipalInformation}
              />
            </Tab>
            <Tab
              key={principalInformationTabConfigs.dataAccess}
              tabKey={principalInformationTabConfigs.dataAccess}
              title={t(
                `features:admin-center:principalAdministration:editPrincipalModal:${principalInformationTabConfigs.dataAccess}:title`,
              )}
            >
              <EditDataAccess
                principalInformation={principalInformation}
                onChange={setPrincipalInformation}
              />
            </Tab>
          </Tabs>
          <hr className="h-1px bg-grey-300 mx-auto w-full rounded border-0" />
        </div>
      </div>
    </Modal>
  );
};
